import { initials, years, branchTotal } from "@/lib/tree";
import { imgSrc } from "@/lib/api";
import { useNetWorth, shortINR, tinyINR } from "@/context/NetWorthContext";
import { t } from "@/lib/i18n";

export const Avatar = ({ p, size = "w-12 h-12" }) =>
  p.photo_url ? (
    <img src={imgSrc(p.photo_url)} alt={p.name} className={`${size} rounded-full object-cover border-2 border-[#FDFBF7]`} />
  ) : (
    <span className={`${size} rounded-full grid place-items-center font-serif font-semibold text-[#FDFBF7] ${p.gender === "female" ? "bg-[#D96B43]" : p.gender === "male" ? "bg-[#1E3A2B]" : "bg-[#C59B27]"}`}>
      {initials(p.name)}
    </span>
  );

const WorthCircle = ({ p, tree }) => {
  const { map, visible } = useNetWorth();
  if (!visible || !tree) return null;
  const b = branchTotal(p.person_id, tree, map);
  return (
    <span data-testid={`person-worth-${p.person_id}`} title={`${t("Lineage net worth")}: ${shortINR(b.total) || "₹0"}`}
      className={`ml-auto w-12 h-12 shrink-0 rounded-full grid place-items-center text-center leading-none ring-2 ring-offset-1 ring-[#C59B27]/50 shadow-[0_4px_14px_-4px_rgba(197,155,39,0.7)]
        ${b.total ? "bg-gradient-to-br from-[#E2BE52] to-[#C59B27] text-[#1C2421]" : "bg-[#F4EDE2] text-[#7E8E85]"}`}>
      <span><span className="block text-[9px] font-mono">₹</span><span className="block font-mono text-[11px] font-bold">{tinyINR(b.total)}</span></span>
    </span>
  );
};

const HoverTip = ({ p, tree }) => {
  const { map, visible } = useNetWorth();
  const nw = map[p.person_id];
  return (
    <span role="tooltip" data-testid={`person-tip-${p.person_id}`}
      className="pointer-events-none absolute left-1/2 top-full mt-2 -translate-x-1/2 -translate-y-1 opacity-0 group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-200 z-30 whitespace-nowrap rounded-xl bg-[#1C2421] text-[#FDFBF7] px-3 py-2 text-left shadow-lg">
      <span className="block font-serif text-sm font-semibold">{p.name}</span>
      {visible && <span className="block font-mono text-[11px] text-[#E2BE52]">{t("Net worth")}: {nw ? shortINR(nw.amount) : t("not added")}</span>}
      {visible && tree && <span className="block font-mono text-[11px] text-[#E2BE52]">{t("Lineage net worth")}: {shortINR(branchTotal(p.person_id, tree, map).total) || "₹0"}</span>}
      <span className="absolute left-1/2 -top-1 -translate-x-1/2 w-2 h-2 rotate-45 bg-[#1C2421]" />
    </span>
  );
};

export const PersonCard = ({ p, onClick, active, dim, spouse, tree }) => (
  <button
    type="button"
    onClick={() => onClick?.(p)}
    data-testid={`person-card-${p.person_id}`}
    className={`group relative flex items-center gap-3 text-left min-w-[150px] max-w-[250px] px-3 py-2.5 rounded-2xl border bg-white
      transition-[transform,box-shadow,opacity,border-color] duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_-12px_rgba(30,58,43,0.35)]
      ${active ? "border-[#D96B43] ring-4 ring-[#D96B43]/15" : "border-[#E5DEC9]"} ${dim ? "dim" : ""} ${spouse ? "bg-[#F4EDE2]/60" : ""}`}
  >
    <HoverTip p={p} tree={tree} />
    <Avatar p={p} size="w-11 h-11 shrink-0" />
    <span className="min-w-0">
      <span className="block font-serif font-semibold text-[15px] leading-tight text-[#1C2421] break-words">{p.name}</span>
      <span className="block font-mono text-[11px] text-[#7E8E85]">{years(p) || "\u00A0"}</span>
    </span>
    <WorthCircle p={p} tree={tree} />
  </button>
);
